/**
 * Driving Mode component - full-screen dark overlay for voice-first chat.
 * Walkie-talkie flow: tap mic -> speak -> tap stop (or silence timeout) -> AI responds via TTS -> auto-listen.
 *
 * Concerns are split across dedicated files:
 * - playback / reading orchestration -> useDrivingModePlayback.ts
 * - amplitude visuals               -> useDrivingModeAmplitude.ts
 * - keypad UI                       -> DrivingModeKeypad.tsx
 * - shared render/text helpers      -> DrivingMode.shared.tsx
 */
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { store } from '../../state/store';
import { t, getDirection } from '../../i18n';
import { micIconLarge, micOffIcon, keypadIcon, sendIcon } from '../icons';
import type { ChatMessage, ToolCallState } from '../../types';
import {
  listAudioOutputDevices,
  supportsAudioOutputSelection,
  type AudioOutputDeviceOption,
} from '../../services/audioOutput';
import { AUTO_SCROLL_THRESHOLD_PX, isNearBottom } from '../../utils/scroll';
import {
  cleanupDrivingMode,
  cleanForDrivingDisplay,
  hasSendableSpeech,
} from './functions/DrivingMode.helpers';
import { DrivingModeSettings } from './drivingModeSettings/DrivingModeSettings';
import { DrivingHeader } from './drivingHeader/DrivingHeader';
import { DrivingModeKeypad } from './DrivingModeKeypad';
import { renderToolCallStatus, resolveDrivingAssistantText } from './DrivingMode.shared';
import { HIDDEN_TOOL_CALL_NAMES } from '../messageBubble/helpers';
import { useDrivingModeAmplitude } from './functions/useDrivingModeAmplitude';
import { useDrivingModePlayback } from './functions/useDrivingModePlayback';

export { cleanupDrivingMode };

function UserAvatar() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

interface InlineSvgIconProps {
  html: string;
  className?: string;
}

const InlineSvgIcon = memo(function InlineSvgIcon({
  html,
  className,
}: InlineSvgIconProps) {
  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
});

interface DrivingKeypadToggleProps {
  label: string;
}

const DrivingKeypadToggle = memo(function DrivingKeypadToggle({
  label,
}: DrivingKeypadToggleProps) {
  const handleClick = useCallback(() => {
    const { keypadOpen } = store.getState().drivingMode;
    store.setDrivingMode({ keypadOpen: !keypadOpen });
  }, []);

  return (
    <button
      className="driving-keypad-toggle"
      aria-label={label}
      title={label}
      onClick={handleClick}
    >
      <InlineSvgIcon html={keypadIcon} />
    </button>
  );
});

interface DrivingMessagesProps {
  chatMessages: ChatMessage[];
  lastAssistantMessageId: string | null;
  lastResponse: string;
  toolCalls: ToolCallState[];
}

const DrivingMessages = memo(function DrivingMessages({
  chatMessages,
  lastAssistantMessageId,
  lastResponse,
  toolCalls,
}: DrivingMessagesProps) {
  return (
    <>
      {chatMessages.length > 0 ? (
        chatMessages.map((message) => {
          const isUser = message.role === 'user';
          const isCurrentAssistantReply = !isUser && message.id === lastAssistantMessageId;
          const sourceText = resolveDrivingAssistantText(
            message,
            lastAssistantMessageId,
            lastResponse,
          );
          const cleaned = cleanForDrivingDisplay(sourceText);
          const showToolCalls = isCurrentAssistantReply && toolCalls.length > 0;

          return (
            <div
              key={message.id}
              className={isUser ? 'driving-msg driving-msg-user' : 'driving-msg driving-msg-assistant'}
            >
              {isUser ? (
                <span className="driving-msg-avatar driving-msg-avatar-user">
                  <UserAvatar />
                </span>
              ) : (
                <span className="driving-msg-avatar driving-msg-avatar-bot" />
              )}
              <div className="driving-msg-body">
                {(cleaned || isCurrentAssistantReply) && (
                  <span
                    className={
                      isCurrentAssistantReply
                        ? 'driving-msg-text driving-msg-text-current'
                        : 'driving-msg-text'
                    }
                  >
                    {cleaned}
                  </span>
                )}
                {showToolCalls && (
                  <div className="driving-tool-calls">
                    {toolCalls.map((toolCall) => (
                      <div
                        key={toolCall.id}
                        className={`driving-tool-call driving-tool-call-${toolCall.status}`}
                      >
                        {renderToolCallStatus(toolCall.status)}
                        <span className="driving-tool-call-name">{toolCall.name}</span>
                        {toolCall.status === 'error' && toolCall.error && (
                          <span className="driving-tool-call-error-text">{toolCall.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className="driving-response-empty">{t('drivingModeEmptyResponse')}</div>
      )}
    </>
  );
});

export function DrivingMode() {
  const transcriptRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const speechDetectedRef = useRef(false);
  const micPulseRef = useRef<HTMLDivElement>(null);
  const micPulseOuterRef = useRef<HTMLDivElement>(null);

  const { drivingMode, messages, toolCalls } = store.getState();
  const visibleToolCalls = toolCalls.filter((toolCall) => !HIDDEN_TOOL_CALL_NAMES.has(toolCall.name));

  const [, forceUpdate] = useState(0);
  const [audioOutputOptions, setAudioOutputOptions] = useState<AudioOutputDeviceOption[]>([]);
  const [audioOutputSupported, setAudioOutputSupported] = useState(false);

  const handleMicClickRef = useRef<() => void>(() => {});

  const { handleMicClick, handleRepeat, handleSendClick } = useDrivingModePlayback({
    transcriptRef,
    chatAreaRef,
    speechDetectedRef,
    forceUpdate,
    speechRate: drivingMode.speechRate || 1,
  });

  handleMicClickRef.current = handleMicClick;

  useDrivingModeAmplitude({
    listening: drivingMode.listening,
    pulseRef: micPulseRef,
    pulseOuterRef: micPulseOuterRef,
  });

  useEffect(() => {
    const supported = supportsAudioOutputSelection();
    setAudioOutputSupported(supported);
    if (!supported) return;

    let active = true;

    const refreshAudioOutputs = async () => {
      const devices = await listAudioOutputDevices();
      if (active) {
        setAudioOutputOptions(devices);
      }
    };

    void refreshAudioOutputs();

    const mediaDevices = navigator.mediaDevices;
    mediaDevices?.addEventListener?.('devicechange', refreshAudioOutputs);

    return () => {
      active = false;
      mediaDevices?.removeEventListener?.('devicechange', refreshAudioOutputs);
    };
  }, []);

  useEffect(() => {
    const root = overlayRef.current?.getRootNode() as ShadowRoot | Document | null;
    const handler = (event: Event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.code === 'Space' && !keyboardEvent.repeat) {
        event.preventDefault();
        handleMicClickRef.current();
      }
    };

    root?.addEventListener('keydown', handler);
    return () => root?.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return;

    const handleScroll = () => {
      shouldAutoScrollRef.current = isNearBottom(chatArea, AUTO_SCROLL_THRESHOLD_PX);
    };

    handleScroll();
    chatArea.addEventListener('scroll', handleScroll, { passive: true });
    return () => chatArea.removeEventListener('scroll', handleScroll);
  }, []);

  const chatMessages = useMemo(() => messages.filter((message) => {
    if (message.isGreeting) return false;
    if (message.role === 'assistant') {
      return message.isStreaming || !!message.content;
    }
    return !!message.content;
  }), [messages]);

  const lastAssistantMessage = useMemo(() => [...chatMessages].reverse().find(
    (message) => message.role === 'assistant',
  ) || null, [chatMessages]);
  const lastAssistantMessageId = lastAssistantMessage?.id || null;
  const toolCallSignature = toolCalls
    .map((toolCall) => `${toolCall.id}:${toolCall.status}:${toolCall.error || ''}`)
    .join('|');

  useLayoutEffect(() => {
    if (chatAreaRef.current && shouldAutoScrollRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [
    chatMessages.length,
    lastAssistantMessageId,
    lastAssistantMessage?.content,
    drivingMode.lastResponse,
    toolCallSignature,
  ]);

  let statusClass = '';
  let statusText = t('drivingModeIdle');
  if (drivingMode.listening) {
    statusClass = 'status-listening';
    statusText = t('drivingModeListening');
  } else if (drivingMode.processing) {
    statusClass = 'status-processing';
    statusText = t('drivingModeProcessing');
  } else if (drivingMode.speaking) {
    statusClass = 'status-speaking';
    statusText = t('drivingModeSpeaking');
  }

  let micClass = '';
  if (drivingMode.listening) micClass = 'mic-listening';
  else if (drivingMode.speaking) micClass = 'mic-speaking';
  else if (drivingMode.processing) micClass = 'mic-processing';

  const micSvg = drivingMode.listening
    ? micOffIcon.replace('width="18"', 'width="36"').replace('height="18"', 'height="36"')
    : micIconLarge;

  const hint = (drivingMode.listening || drivingMode.speaking || drivingMode.processing)
    ? t('drivingModeTapToStop')
    : t('drivingModeTapToSpeak');

  const errorText = drivingMode.error
    ? (t(drivingMode.error as never) || drivingMode.error)
    : null;
  const sendDisabled = !drivingMode.listening || !hasSendableSpeech(speechDetectedRef);

  const handleExit = useCallback((): void => {
    cleanupDrivingMode();
    if (transcriptRef.current) {
      transcriptRef.current.textContent = '';
    }
    store.setDrivingMode({
      active: false,
      listening: false,
      speaking: false,
      processing: false,
      transcript: '',
      error: null,
      keypadOpen: false,
    });
  }, []);

  return (
    <div className="driving-overlay" ref={overlayRef} dir={getDirection()}>
      <DrivingHeader
        title={t('drivingMode')}
        exitLabel={t('drivingModeExit')}
        statusClass={statusClass}
        statusText={statusText}
        onExit={handleExit}
      />

      <div className="driving-chat-area" ref={chatAreaRef}>
        <DrivingMessages
          chatMessages={chatMessages}
          lastAssistantMessageId={lastAssistantMessageId}
          lastResponse={drivingMode.lastResponse}
          toolCalls={visibleToolCalls}
        />
      </div>

      <div className="driving-transcript-area">
        <div className="driving-transcript" ref={transcriptRef} />
      </div>

      <div className="driving-mic-area">
        <div className="driving-mic-row">
          <DrivingKeypadToggle label={t('keypadOpen')} />
          <button className={`driving-mic-btn ${micClass}`} onClick={handleMicClick}>
            <div className="driving-mic-pulse" ref={micPulseRef} />
            <div className="driving-mic-pulse-outer" ref={micPulseOuterRef} />
            <InlineSvgIcon html={micSvg} />
          </button>
          <button
            className="driving-send-btn"
            aria-label={t('send')}
            title={t('send')}
            onClick={handleSendClick}
            disabled={sendDisabled}
          >
            <InlineSvgIcon html={sendIcon} />
          </button>
        </div>
        <div className="driving-mic-hint">{hint}</div>
        {drivingMode.lastResponse && !drivingMode.listening && !drivingMode.processing && (
          <button
            className="driving-repeat-btn"
            onClick={handleRepeat}
            title={t('drivingModeRepeat')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 .49-3.26" />
            </svg>
            {t('drivingModeRepeat')}
          </button>
        )}
        <DrivingModeSettings
          audioOutputSupported={audioOutputSupported}
          audioOutputOptions={audioOutputOptions}
          speechRate={drivingMode.speechRate}
          audioOutputDeviceId={drivingMode.audioOutputDeviceId}
          drivingCueSoundsEnabled={drivingMode.drivingCueSoundsEnabled}
        />
      </div>

      {drivingMode.keypadOpen && <DrivingModeKeypad />}
      {errorText && <div className="driving-error">{errorText}</div>}
    </div>
  );
}
